import type { MapLocation } from '@/types';

export interface Point {
  x: number;
  y: number;
}

/**
 * Detects the closed regions ("faces") of the explored map graph: every area
 * whose entire boundary consists of explored locations connected by explored
 * links. These polygons can be cleared from the fog so the interior between a
 * fully-discovered ring of towns becomes visible.
 *
 * Coordinates are passed through in the source `loc.x`/`loc.y` space (0-100);
 * the caller scales them to map pixels.
 *
 * Algorithm: build the undirected explored subgraph, split each edge into two
 * directed half-edges, then trace minimal planar faces by always taking the
 * next neighbour clockwise around the arrival node. With this rule every
 * bounded (interior) face is wound so its signed area is positive in screen
 * coordinates, while the unbounded outer face of each component is negative, so
 * we simply keep the positive-area faces.
 */
export function computeExploredFaces(
  locations: MapLocation[],
  explored: Set<string>,
): Point[][] {
  const nodes = new Map<string, Point>();
  for (const loc of locations) {
    if (explored.has(loc.id)) nodes.set(loc.id, { x: loc.x, y: loc.y });
  }
  if (nodes.size < 3) return [];

  // Undirected adjacency over explored nodes (deduped).
  const adj = new Map<string, Set<string>>();
  const addEdge = (a: string, b: string) => {
    if (a === b || !nodes.has(a) || !nodes.has(b)) return;
    if (!adj.has(a)) adj.set(a, new Set());
    if (!adj.has(b)) adj.set(b, new Set());
    adj.get(a)!.add(b);
    adj.get(b)!.add(a);
  };
  for (const loc of locations) {
    if (!explored.has(loc.id)) continue;
    for (const other of loc.connectedTo ?? []) {
      if (explored.has(other)) addEdge(loc.id, other);
    }
  }

  // For each node, neighbours ordered CCW by angle, plus neighbour -> index.
  const orderedNeighbours = new Map<string, string[]>();
  const neighbourIndex = new Map<string, Map<string, number>>();
  for (const [id, neighbours] of adj) {
    const self = nodes.get(id)!;
    const sorted = [...neighbours].sort((a, b) => {
      const na = nodes.get(a)!;
      const nb = nodes.get(b)!;
      return (
        Math.atan2(na.y - self.y, na.x - self.x) -
        Math.atan2(nb.y - self.y, nb.x - self.x)
      );
    });
    orderedNeighbours.set(id, sorted);
    const idx = new Map<string, number>();
    sorted.forEach((n, i) => idx.set(n, i));
    neighbourIndex.set(id, idx);
  }

  const visited = new Set<string>();
  const key = (from: string, to: string) => `${from}\u0000${to}`;
  const faces: Point[][] = [];
  const totalHalfEdges = [...adj.values()].reduce((s, set) => s + set.size, 0);

  for (const [start, neighbours] of adj) {
    for (const first of neighbours) {
      if (visited.has(key(start, first))) continue;
      const poly: Point[] = [];
      let from = start;
      let to = first;
      let guard = 0;
      while (!visited.has(key(from, to)) && guard <= totalHalfEdges) {
        visited.add(key(from, to));
        poly.push(nodes.get(from)!);
        // Next half-edge: at `to`, take the neighbour clockwise from `from`.
        // This traces minimal interior faces (positive signed area).
        const ordered = orderedNeighbours.get(to)!;
        const idx = neighbourIndex.get(to)!.get(from)!;
        const next = ordered[(idx - 1 + ordered.length) % ordered.length];
        from = to;
        to = next;
        guard += 1;
      }
      if (poly.length >= 3 && signedArea(poly) > 1e-6) faces.push(poly);
    }
  }

  return faces;
}

function signedArea(poly: Point[]): number {
  let area = 0;
  for (let i = 0; i < poly.length; i += 1) {
    const a = poly[i];
    const b = poly[(i + 1) % poly.length];
    area += a.x * b.y - b.x * a.y;
  }
  return area / 2;
}
